import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';

function formatAverage(seconds, t) {
  if (seconds === null) return t('Building average');
  return t('{{count}} sec / order', { count: seconds });
}

function ordinal(value) {
  const remainder100 = value % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${value}th`;

  const suffixes = {
    1: 'st',
    2: 'nd',
    3: 'rd',
  };

  return `${value}${suffixes[value % 10] || 'th'}`;
}

function FirstPlaceShell() {
  return (
    <svg
      className="today-packer-tile__first-place-shell"
      viewBox="0 0 140 112"
      aria-hidden="true"
      focusable="false"
    >
      <path className="shell-speed-line" d="M7 45h28M1 58h28M11 71h24" />
      <path className="shell-wing" d="M48 49 23 29 10 31l17 18-20-8 6 14 27 10Z" />
      <path className="shell-wing" d="m92 49 25-20 13 2-17 18 20-8-6 14-27 10Z" />
      <path className="shell-spike" d="m70 8 8 22H62Z" />
      <path className="shell-spike" d="m44 17 17 18-25-4Z" />
      <path className="shell-spike" d="m96 17-17 18 25-4Z" />
      <path className="shell-body" d="M34 65c0-24 15-39 36-39s36 15 36 39v13H34Z" />
      <path className="shell-panel" d="m70 29-13 18 13 15 13-15Zm-16 21-17 13 18 10 12-27Zm32 0 17 13-18 10-12-27Z" />
      <path className="shell-rim" d="M29 75c0-6 5-10 11-10h60c6 0 11 4 11 10v8c0 7-6 12-13 12H42c-7 0-13-5-13-12Z" />
      <circle className="shell-eye" cx="51" cy="81" r="4" />
      <circle className="shell-eye" cx="89" cy="81" r="4" />
      <path className="shell-banner" d="M10 88h120l-10 11 10 11H10l10-11Z" />
      <text className="shell-banner-text" x="70" y="105" textAnchor="middle">1ST PLACE</text>
    </svg>
  );
}

function ActivePackersWidget({ refreshKey, t }) {
  const [packers, setPackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ranksByPacker = useMemo(() => {
    const ranked = [...packers]
      .filter((packer) => packer.average_seconds_per_order !== null
        && Number.isFinite(Number(packer.average_seconds_per_order)))
      .sort((left, right) => (
        Number(left.average_seconds_per_order) - Number(right.average_seconds_per_order)
      ));
    const ranks = new Map();
    let previousAverage = null;
    let previousRank = null;

    ranked.forEach((packer, index) => {
      const average = Number(packer.average_seconds_per_order);
      const rank = average === previousAverage ? previousRank : index + 1;
      ranks.set(packer.packer, rank);
      previousAverage = average;
      previousRank = rank;
    });

    return ranks;
  }, [packers]);

  useEffect(() => {
    let mounted = true;

    async function loadPackers() {
      try {
        const response = await get('/api/today/active-packers');
        if (mounted) {
          setPackers(Array.isArray(response.packers) ? response.packers : []);
          setLoading(false);
          setError(false);
        }
      } catch (requestError) {
        if (mounted) {
          setLoading(false);
          setError(true);
        }
      }
    }

    loadPackers();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  return (
    <div className="today-active-packers">
      <header className="today-active-packers__header">
        <div>
          <span>{t('Pack stations')}</span>
          <h2>{t('Active Packers')}</h2>
        </div>
        <strong>{packers.length}</strong>
      </header>

      {loading && <div className="today-active-packers__message">{t('Loading active packers…')}</div>}
      {error && <div className="today-active-packers__message is-error">{t('Active packers unavailable')}</div>}
      {!loading && !error && packers.length === 0 && (
        <div className="today-active-packers__message">{t('No packing activity in the last 20 minutes')}</div>
      )}

      {!loading && !error && packers.length > 0 && (
        <div className="today-active-packers__grid">
          {packers.map((packer) => (
            <article className="today-packer-tile" key={packer.packer}>
              <div className="today-packer-tile__person">
                <i className="mdi mdi-package-variant" aria-hidden="true" />
                <strong title={packer.packer}>{packer.packer}</strong>
              </div>

              <div className="today-packer-tile__route">
                <span>{`#${packer.pickroute_id}`}</span>
                <span>{packer.pickroute_type}</span>
              </div>

              <div className="today-packer-tile__cart">
                <small>{t('Last cart')}</small>
                <strong>{packer.cart || '—'}</strong>
              </div>

              <div
                className="today-packer-tile__progress"
                aria-label={t('{{packed}} of {{total}} orders packed', {
                  packed: packer.packed_orders,
                  total: packer.total_orders,
                })}
              >
                <strong>{`${packer.packed_orders}/${packer.total_orders}`}</strong>
                <span>{t('Packed')}</span>
              </div>

              <div className="today-packer-tile__average">
                <i className="mdi mdi-timer-outline" aria-hidden="true" />
                <span>{formatAverage(packer.average_seconds_per_order, t)}</span>
                {ranksByPacker.has(packer.packer) && (
                  <strong
                    className={`today-packer-tile__rank${ranksByPacker.get(packer.packer) === 1 ? ' is-first' : ''}`}
                    aria-label={t('{{rank}} place among active packers', {
                      rank: ordinal(ranksByPacker.get(packer.packer)),
                    })}
                  >
                    {ranksByPacker.get(packer.packer) === 1 && (
                      <i className="mdi mdi-star" aria-hidden="true" />
                    )}
                    {ordinal(ranksByPacker.get(packer.packer))}
                  </strong>
                )}
              </div>

              {packer.total_orders > 0 && (
                <div
                  className="today-packer-tile__progress-bar"
                  role="progressbar"
                  aria-label={t('Route packing progress')}
                  aria-valuemin="0"
                  aria-valuemax={packer.total_orders}
                  aria-valuenow={packer.packed_orders}
                >
                  <span
                    style={{
                      width: `${Math.min(
                        100,
                        (packer.packed_orders / packer.total_orders) * 100,
                      )}%`,
                    }}
                  />
                </div>
              )}

              {ranksByPacker.get(packer.packer) === 1 && <FirstPlaceShell />}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

ActivePackersWidget.defaultProps = {
  t: (value) => value,
};

ActivePackersWidget.propTypes = {
  refreshKey: PropTypes.number.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(ActivePackersWidget);
